import { type CSSProperties, memo, useId, useMemo } from 'react'
import { emblemPath, frameOf } from '@/features/shop/skins'
import type {
  BoardGeometry,
  BoardGuide,
  BoardPerson,
  BoardPlayer,
  Cell,
  MoveTarget,
  PlacedToken,
  PlayerColor,
  Rect,
} from '@/shared/board/types'
import { fitName, textWidth } from './name'
import { useFontsReady } from './useFontsReady'

/**
 * Отрисовка доски.
 *
 * Единица SVG = одна клетка, поэтому координаты с бэкенда ложатся в разметку
 * без пересчёта: col — это x, row — это y. Размер на экране задаётся снаружи
 * шириной элемента, а не константой в пикселях.
 *
 * Порядок слоёв важен. Метки целей рисуются ПОД фишками, иначе они закрыли бы
 * фишку, которую собираются срубить. А ловушка нажатия — НАД фишками, целой
 * клеткой: иначе чужая фишка перехватывает клик, и срубить её можно было
 * только попав в угол клетки мимо неё.
 *
 * Кликабельные фишки и цели — группы <g> с role="button": настоящей кнопки
 * внутри SVG не существует, а рисовать их вне картинки значило бы потерять
 * привязку к клетке. Правило useSemanticElements для этого файла выключено
 * в biome.json.
 */

/**
 * Цвета игроков.
 *
 * Насыщенный тон берётся из темы (в тёмной он светлее — иначе красный
 * читается как бурый). Кант фишки получается из него же затемнением:
 * color-mix избавляет от второго набора значений, который пришлось бы
 * держать в согласии с первым.
 *
 * Тонированной заливки больше нет. Раньше ею закрашивались клетки кольца,
 * дома и старта — то есть почти вся доска; теперь цвет живёт там, где он
 * что-то значит: на фишке, на воротах старта, на ленте дома и на подносе
 * своего двора, а прозрачность задаётся на месте.
 */
const PALETTE: Record<PlayerColor, { line: string; fill: string; rim: string }> = {
  red: { line: 'var(--red)', fill: 'var(--red)', rim: 'color-mix(in srgb, var(--red) 62%, #000)' },
  blue: {
    line: 'var(--blue)',
    fill: 'var(--blue)',
    rim: 'color-mix(in srgb, var(--blue) 62%, #000)',
  },
  green: {
    line: 'var(--green)',
    fill: 'var(--green)',
    rim: 'color-mix(in srgb, var(--green) 62%, #000)',
  },
  yellow: {
    line: 'var(--yellow)',
    fill: 'var(--yellow)',
    rim: 'color-mix(in srgb, var(--yellow) 62%, #000)',
  },
}

const LABEL: Record<PlayerColor, string> = {
  red: 'красный',
  blue: 'синий',
  green: 'зелёный',
  yellow: 'жёлтый',
}

/** Цвет метки: рубка — красная, прыжок по углам — коричневый, как сами углы. */
function targetColor(target: MoveTarget): string {
  if (target.victims.length > 0) return 'var(--red)'

  return target.jump ? 'var(--yellow)' : 'var(--mark)'
}

/**
 * Значки бонусов в координатах клетки: центр в нуле, размах ±0.2.
 * Рисуются белым по кружку акцентного цвета — отличаются и от жёлтых колец
 * углов, и от меток целей.
 */
const GLYPHS: Record<string, string> = {
  // Сундук: короб и крышка.
  chest: 'M-0.18 -0.04 H0.18 V0.16 H-0.18 Z M-0.18 -0.08 Q0 -0.22 0.18 -0.08 Z',
  // Доброс: кубик с одной точкой — «ещё бросок».
  roll: 'M-0.15 -0.15 H0.15 V0.15 H-0.15 Z',
  // Щит.
  shield: 'M0 -0.2 L0.17 -0.13 V0.01 Q0.17 0.14 0 0.21 Q-0.17 0.14 -0.17 0.01 V-0.13 Z',
}

/** Значок бонуса на клетке. */
function BonusGlyph({ kind }: { kind: string }) {
  return (
    <>
      <circle r={0.4} className="bonus__disc" />
      {/* Глиф крупнее своих координат: на телефоне клетка — 14 пикселей,
          и значок в натуральную величину читался точкой. */}
      <g transform="scale(1.35)">
        <path d={GLYPHS[kind] ?? GLYPHS.chest} className="bonus__glyph" />
        {kind === 'roll' && <circle r={0.05} className="bonus__pip" />}
      </g>
    </>
  )
}

/** Центр клетки — им пользуются фишки, метки целей и подписи. */
function center(cell: Cell): { x: number; y: number } {
  return { x: cell.col + 0.5, y: cell.row + 0.5 }
}

/*
 * Зона нажатия шире того, что нарисовано.
 *
 * Доска — 25 клеток в ширину, и на телефоне одна клетка выходит около 14
 * пикселей, а кружок фишки внутри неё — меньше. Пальцем в такое не
 * попасть: рекомендованный минимум для касания — 44 пикселя, и увеличить
 * доску нечем, она уже занимает всю ширину экрана.
 *
 * Запас нельзя дать всем поровну — на это уже наступали: цели ходов часто
 * стоят на соседних клетках, и фиксированный запас уводил нажатие не той
 * цели. Поэтому радиус у каждой точки свой: половина расстояния до
 * ближайшей другой нажимаемой точки, но не больше MAX и не меньше MIN.
 * Половина — потому что при таком радиусе зоны двух соседей смыкаются, но
 * не заходят друг на друга, и нажатие всегда достаётся ближайшему.
 *
 * Клетки целые, поэтому шкала на деле в две ступени: у точек по соседству
 * (расстояние 1) радиус упирается в MIN, у всех прочих — в MAX.
 *
 * Потолок нужен ради одиноких точек: единственной цели на доске соседей нет
 * вовсе, и без него зона растянулась бы на полдоски. Двух клеток хватает —
 * это около 29 пикселей на телефоне против 13 у нарисованного кружка фишки,
 * и до кружка ближайшего соседа (он в 1.62 клетки) зона всё ещё не доходит.
 */
const MAX_REACH = 1

/*
 * Меньше клетки не даём: ровно клетка была у целей и раньше, и сужать её
 * из-за близкого соседа значило бы менять поведение к худшему. Кружок фишки
 * (r = 0.44) и кольцо готовности (r = 0.5) при этом остаются внутри своей
 * зоны — так и надо: нарисованное не должно обещать больше, чем ловится.
 */
const MIN_REACH = 0.5

/**
 * Радиус зоны нажатия для каждой нажимаемой точки, в клетках.
 *
 * Расстояние считается по Чебышёву (max по осям), а не по прямой: клетки
 * лежат сеткой, и по диагонали сосед ничуть не дальше, чем по стороне.
 *
 * Совпадающие клетки (dist = 0) не учитываются. Так бывает, когда цель хода
 * стоит на клетке своей же фишки — блок из двух фишек. Спор между ними уже
 * решён порядком слоёв: ловушка цели рисуется поверх фишек. Учитывать такую
 * пару здесь значило бы обнулить радиус обеим.
 */
function reaches(spots: { key: string; cell: Cell }[]): Map<string, number> {
  const out = new Map<string, number>()

  spots.forEach((spot, index) => {
    let nearest = Number.POSITIVE_INFINITY

    spots.forEach((other, at) => {
      if (at === index) return

      const dist = Math.max(
        Math.abs(other.cell.col - spot.cell.col),
        Math.abs(other.cell.row - spot.cell.row),
      )

      if (dist > 0 && dist < nearest) nearest = dist
    })

    out.set(
      spot.key,
      Number.isFinite(nearest) ? Math.min(MAX_REACH, Math.max(MIN_REACH, nearest / 2)) : MAX_REACH,
    )
  })

  return out
}

/**
 * Пиксели телефонной доски в клетки.
 *
 * Доска рисуется в клетках, но карточка игрока и поднос под кубики заданы
 * в пикселях: у креста клетка 13,8 пикселя, у меандра 20, и одни и те же
 * доли клетки дали бы буквы полуторного размера. Делитель — ширина viewBox
 * (сторона доски плюс поле в полклетки с каждой стороны, см. BoardView),
 * а 360 — ширина доски на телефоне.
 */
function inCells(board: BoardGeometry, px: number): number {
  return (px * (board.size + 1)) / 360
}

/** Ключ фишки — он же ключ её зоны нажатия. */
function tokenKey(placed: PlacedToken): string {
  return `token-${placed.color}-${placed.token}`
}

/**
 * Прозрачная зона нажатия вокруг центра клетки.
 *
 * Квадратом, а не кругом: у клеток квадратная сетка, и в углах между четырьмя
 * зонами круги оставляли бы мёртвые точки, куда нажатие не попадает ни к кому.
 */
function Reach({ cell, radius }: { cell: Cell; radius: number }) {
  const point = center(cell)

  return (
    <rect
      x={point.x - radius}
      y={point.y - radius}
      width={radius * 2}
      height={radius * 2}
      fill="transparent"
    />
  )
}

/**
 * Насколько опустить строку, в долях кегля, чтобы она встала серединой на свою
 * точку.
 *
 * Вместо dominant-baseline="central": Safari считает его по-своему, и строка
 * уезжает от своей точки — на WebKit намерено 0,36 кегля в меандре и 0,15
 * в кресте. Величина зависит от масштаба viewBox, поэтому в разных видах поля
 * съезжало по-разному, и заметнее всего на букве в кружке аватара: кружок
 * стоит на месте, а буква — нет.
 *
 * 0,35 кегля — половина высоты прописной буквы. От того, что делает central
 * (середина между выносными элементами шрифта), это отличается меньше чем
 * на полпикселя, зато считается одинаково во всех браузерах: dy — обычная
 * длина, а не свойство выравнивания. После правки промах и там, и там —
 * полпикселя.
 */
const BASELINE = 0.35

/** Сторона подноса двора и поле от него до края двора, в пикселях доски. */
const YARD = 88
const YARD_EDGE = 12

/**
 * Поднос двора — квадрат, в котором стоит карточка игрока.
 *
 * Размер один при любом виде поля и задан в пикселях: двор у креста — угловой
 * квадрат в 10 клеток, у меандра карман в 5, и «доля двора» давала два разных
 * блока игрока там, где блок один и тот же. Двор поменьше всё-таки главнее
 * числа: поднос обязан поместиться в него с полем по краю.
 *
 * Считается в одном месте, потому что нужен в трёх: заливка двора, карточка
 * внутри и зона нажатия с пульсирующим кольцом — они обязаны совпадать.
 */
function yardBox(
  board: BoardGeometry,
  rect: Rect,
): { x: number; y: number; width: number; height: number } {
  const side = Math.min(inCells(board, YARD), rect.width - inCells(board, YARD_EDGE))

  return {
    x: rect.col + (rect.width - side) / 2,
    y: rect.row + (rect.height - side) / 2,
    width: side,
    height: side,
  }
}

/**
 * Размеры карточки — в ПИКСЕЛЯХ телефонной доски, одни на все виды поля.
 *
 * Мерить их двором нельзя, а раньше мерились: у креста двор — угловой квадрат
 * в 10 клеток, у меандра карман в 5. Один и тот же игрок получал в кресте имя
 * полным кеглем во всю ширину двора, а в меандре — уменьшенное и обрезанное,
 * хотя карточка в обоих случаях про одно и то же.
 *
 * Поле по бокам (`room` уже, чем поднос) — оттуда же: имя во всю ширину
 * упиралось в скруглённый угол подноса и читалось как вылезшее за него.
 */
/** Толщина рамки аватара из магазина, в пикселях телефонной доски. */
const FRAME = 3

const CARD = {
  /** Радиус кружка с буквой. */
  avatar: 13,
  /**
   * Ширина, в которую вписывается имя.
   *
   * Поднос двора — 88 (YARD), и четыре пикселя по бокам оставлены нарочно:
   * имя во всю ширину упиралось в скруглённый угол и читалось как вылезшее
   * за него. Шире делать нечего, а от этой ширины зависит потолок кегля
   * имени (START в name.ts).
   */
  room: 80,
  /** От кружка до имени. */
  gap: 7,
  /** От имени до счёта. */
  lead: 4,
  /** Кегль счёта «во дворе N». */
  count: 13,
}

/**
 * Карточка игрока в его дворе: аватар, имя и сколько фишек ещё во дворе.
 *
 * Столбиком по центру: у карточки нет содержимого, которое тянулось бы влево,
 * и прижатый к углу столбик читался как сдвинутый. Двор при этом квадратный
 * при любом виде поля, так что центр везде один и тот же.
 *
 * Ждущие фишки — числом, а не пятью кружками. Кружки занимали строку, которая
 * нужна имени, а отличить одну ждущую фишку от другой всё равно нельзя.
 */
function Card({
  board,
  colour,
  person,
  box,
}: {
  board: BoardGeometry
  colour: PlayerColor
  person: BoardPerson
  box: { x: number; y: number; width: number; height: number }
}) {
  const cells = (px: number) => inCells(board, px)
  const skin = PALETTE[colour]
  const name = fitName(person.name, CARD.room)
  const avatar = cells(CARD.avatar)
  const frame = frameOf(person.frame)
  /*
   * Рамка ложится наружу от кружка и входит в высоту блока: иначе она
   * съедала зазор до имени, и имя прижималось к рамке. Блок с рамкой по
   * центру двора так же, как и без неё.
   */
  const rim = frame === null ? 0 : cells(FRAME)
  const block = cells(CARD.avatar * 2 + CARD.gap + name.size + CARD.lead + CARD.count) + rim * 2
  const top = box.y + (box.height - block) / 2
  // Верх кружка аватара — ниже верха блока на толщину рамки.
  const face = top + rim
  const middle = box.x + box.width / 2
  const letter = name.text.slice(0, 1).toUpperCase()
  // Кружок обрезает картинку по себе. Свой на игрока: цвет на доске уникален,
  // а общий clipPath пришлось бы двигать под каждый двор.
  const mask = `ludo-avatar-${colour}`

  return (
    <>
      <circle cx={middle} cy={face + avatar} r={avatar} fill={skin.fill} />
      {/*
        Картинка, если человек её поставил, — как и в кружке рядом с ником
        (features/auth/Avatar.tsx). Буква под ней не рисуется: сквозь картинку
        её не видно, а если картинка не загрузится, останется кружок своего
        цвета — на доске он и так значит игрока.

        slice, а не meet: аватарки не обязаны быть квадратными, и вписанная
        целиком оставляла бы поля внутри кружка.
      */}
      {person.avatar === null ? (
        <text
          className="board__card"
          x={middle}
          y={face + avatar}
          dy={avatar * 1.15 * BASELINE}
          textAnchor="middle"
          fontSize={avatar * 1.15}
          fill="var(--card)"
        >
          {letter}
        </text>
      ) : (
        <>
          <clipPath id={mask}>
            <circle cx={middle} cy={face + avatar} r={avatar} />
          </clipPath>
          <image
            href={person.avatar}
            x={middle - avatar}
            y={face}
            width={avatar * 2}
            height={avatar * 2}
            preserveAspectRatio="xMidYMid slice"
            clipPath={`url(#${mask})`}
          />
        </>
      )}
      {/*
        Рамка из магазина — поверх картинки, по её краю: широкое кольцо и
        тонкая кромка внутри, чтобы рамка не сливалась с аватаркой. Рисуется
        наружу от кружка, а не внутрь: картинка остаётся прежнего размера.
      */}
      {frame !== null && (
        <>
          <circle
            cx={middle}
            cy={face + avatar}
            r={avatar + rim / 2}
            fill="none"
            stroke={frame.ring}
            strokeWidth={rim}
          />
          <circle
            cx={middle}
            cy={face + avatar}
            r={avatar}
            fill="none"
            stroke={frame.edge}
            strokeWidth={cells(1)}
          />
        </>
      )}
      <text
        className="board__card"
        x={middle}
        y={face + avatar * 2 + rim + cells(CARD.gap) + cells(name.size) / 2}
        dy={cells(name.size) * BASELINE}
        textAnchor="middle"
        fontSize={cells(name.size)}
        fill="var(--ink)"
      >
        {name.text}
      </text>
      {/* Счёт своим цветом, пока во дворе кто-то есть: это подсказка к ходу,
          а не подпись. Пустой двор уходит в серый — заводить больше нечего. */}
      <text
        className="board__count"
        x={middle}
        y={top + block - cells(CARD.count) / 2}
        dy={cells(CARD.count) * BASELINE}
        textAnchor="middle"
        fontSize={cells(CARD.count)}
        fill={person.waiting > 0 ? skin.line : 'var(--muted)'}
      >
        {person.waiting > 0 ? `во дворе ${person.waiting}` : 'двор пуст'}
      </text>
    </>
  )
}

/**
 * Середина двора — куда фишка уезжает, уходя со сцены, и откуда выезжает.
 *
 * Мест во дворе больше нет, их заменил счёт в карточке, и срубленной фишке
 * стало некуда возвращаться: она просто исчезала на месте. Теперь она уходит
 * в свою карточку — а сам полёт делает CSS: меняется transform, а переход
 * у фишки уже есть.
 *
 * Середина, а не аватар: точное место внутри карточки зависит от того, каким
 * кеглем влезло имя, и считать это снаружи значило бы повторить всю её
 * разметку.
 */
function cardCentre(board: BoardGeometry, colour: PlayerColor): { x: number; y: number } {
  const rect = board.players.find((one) => one.color === colour)?.base.rect

  if (rect === undefined) return { x: board.size / 2, y: board.size / 2 }

  return { x: rect.col + rect.width / 2, y: rect.row + rect.height / 2 }
}

/**
 * Куда смотрит стрелка на клетке: угол в градусах по шагу.
 *
 * Шаг всегда на соседнюю клетку по стороне, поэтому вариантов ровно четыре,
 * и таблица углов тут была бы длиннее самой проверки.
 */
function angleOf(dx: number, dy: number): number {
  if (dx > 0) return 0
  if (dy > 0) return 90
  if (dx < 0) return 180

  return 270
}

/** Прямоугольник, накрывающий дорожку из клеток в ряд. */
function span(cells: Cell[]): { x: number; y: number; width: number; height: number } {
  const rows = cells.map((cell) => cell.row)
  const cols = cells.map((cell) => cell.col)
  const top = Math.min(...rows)
  const left = Math.min(...cols)

  return {
    x: left,
    y: top,
    width: Math.max(...cols) - left + 1,
    height: Math.max(...rows) - top + 1,
  }
}

/**
 * Всё, что на доске не двигается: плита, дворы, дорожки, центр, дорога.
 *
 * Вынесено в memo не для красоты: при анимации хода слой перерисовывался на
 * каждом шаге и фишка дёргалась. Геометрия за партию не меняется ни разу,
 * поэтому слой считается один раз.
 *
 * Что здесь НЕ рисуется и почему:
 *
 * - Неигровые клетки внутри лучей. Их 140, каждая была прямоугольником
 *   с обводкой, и именно из них складывалась «сетка», в которой фишка
 *   размером в десять пикселей тонула.
 * - Клетки кольца по одной. Их 96, и вместо 96 рамок со скруглением
 *   в 1,7 пикселя рисуется одна дорога с точками-делениями.
 *
 * Обводок в статике осталось четыре (по одной на поднос двора) плюс кромка
 * дороги — против 281 прежней.
 */
const BoardStatic = memo(function BoardStatic({
  board,
  people,
  current,
}: {
  board: BoardGeometry
  people: BoardPerson[]
  current: PlayerColor | null
}) {
  /*
   * Старты и входы ищутся по индексу в кольце — строим обратный указатель,
   * чтобы не бегать по игрокам на каждой клетке. У входа хранится сам игрок:
   * от входа его дорожка уходит внутрь, и стрелка должна смотреть туда же.
   */
  const marks = useMemo(() => {
    const starts = new Map<number, PlayerColor>()
    const entries = new Map<number, BoardPlayer>()

    for (const player of board.players) {
      starts.set(player.startIndex, player.color)
      entries.set(player.entryIndex, player)
    }

    return { starts, entries }
  }, [board.players])

  const seated = useMemo(() => new Map(people.map((person) => [person.color, person])), [people])

  // Имя в карточке подгоняется измерением, а мерить надо тем шрифтом, каким
  // его нарисуют: после загрузки Nunito карточки считаются заново.
  useFontsReady()

  /* Дорога — одной линией по центрам клеток кольца. Строка выходит длинной,
     а кольцо за партию не меняется, поэтому считается один раз. */
  const road = useMemo(
    () => `M ${board.ring.map((cell) => `${cell.col + 0.5} ${cell.row + 0.5}`).join(' L ')} Z`,
    [board.ring],
  )

  const middle = board.size / 2
  // Поднос под кубики — в пикселях: сами кубики (.centre-dice .die) заданы
  // в долях ширины доски, и в клетках поднос выходил бы разным на разных
  // видах поля.
  const tray = inCells(board, 88)

  /*
   * Силуэт креста — одной фигурой.
   *
   * Рисуется только там, где лучи описывают эту доску: у компактных видов
   * поля (features/board/layout.ts) лучей нет вовсе, и признак этого —
   * сходится ли сторона доски из двух лучей и торца. Флага в геометрии для
   * этого не заводим: он повторял бы то, что и так посчитано.
   */
  const arm = board.armLength
  const across = arm + board.armWidth
  const hasArms = arm * 2 + board.armWidth === board.size
  const cross =
    `M ${arm} 0 H ${across} V ${arm} H ${board.size} V ${across} H ${across} ` +
    `V ${board.size} H ${arm} V ${across} H 0 V ${arm} H ${arm} Z`

  return (
    <>
      {/* Плита: всё остальное лежит на ней. */}
      <rect
        x={0}
        y={0}
        width={board.size}
        height={board.size}
        rx={board.size * 0.055}
        fill="var(--plate)"
      />

      {hasArms && <path d={cross} fill="var(--lane)" />}

      {/* Дворы: цвет живёт в своём углу, а не растекается по доске. */}
      {board.players.map((player) => {
        const skin = PALETTE[player.color]
        const { rect } = player.base
        const person = seated.get(player.color)
        /*
         * Чей ход — подсвеченным углом.
         *
         * Ленты игроков снизу больше нет: имена уехали в дворы, и повторять
         * их полосой значило бы говорить одно и то же дважды. Подсветка
         * должна быть заметной, но не спорить с фишками, поэтому меняются
         * плотность заливки и толщина обводки, а не цвет.
         */
        const active = current === player.color
        const box = yardBox(board, rect)

        return (
          <g key={`base-${player.color}`}>
            <rect
              x={box.x}
              y={box.y}
              width={box.width}
              height={box.height}
              rx={Math.min(1, box.width * 0.2)}
              fill={skin.fill}
              fillOpacity={active ? 0.2 : 0.09}
              stroke={skin.line}
              strokeOpacity={active ? 0.85 : 0.3}
              strokeWidth={active ? 0.11 : 0.06}
            />
            {person === undefined ? (
              /* Стол ещё не собран — рисуем пустые места, чтобы двор не был
                 просто цветным пятном. Контуром: залитый кружок сливался
                 с подносом того же цвета. */
              player.base.slots.map((slot) => {
                const point = center(slot)

                return (
                  <circle
                    key={`slot-${player.color}-${slot.row}-${slot.col}`}
                    cx={point.x}
                    cy={point.y}
                    r={0.44}
                    fill="none"
                    stroke={skin.line}
                    strokeOpacity={0.5}
                    strokeWidth={0.06}
                  />
                )
              })
            ) : (
              <Card board={board} colour={player.color} person={person} box={box} />
            )}
          </g>
        )
      })}

      {/*
        Центр — поднос РОВНО под кубики, а не плита во всю свободную середину.
        Свободный квадрат у обоих видов поля одинаковый, семь клеток, но
        у классики это 28 % стороны, а у меандра 41 %: одна и та же плита там
        подпирала дорогу вплотную и читалась как обрезающая поле.
      */}
      <rect
        x={middle - tray / 2}
        y={middle - tray / 2}
        width={tray}
        height={tray}
        rx={inCells(board, 14)}
        fill="var(--centre)"
      />

      {/* Домашние дорожки — по ленте на игрока вместо пяти обведённых клеток. */}
      {board.players.map((player) => {
        const skin = PALETTE[player.color]
        const box = span(player.home)

        return (
          <g key={`home-${player.color}`}>
            <rect
              x={box.x + 0.09}
              y={box.y + 0.09}
              width={box.width - 0.18}
              height={box.height - 0.18}
              rx={0.44}
              fill={skin.fill}
              fillOpacity={0.26}
            />
            {player.home.map((cell) => {
              const point = center(cell)

              return (
                <circle
                  key={`home-slot-${player.color}-${cell.row}-${cell.col}`}
                  cx={point.x}
                  cy={point.y}
                  r={0.11}
                  fill={skin.fill}
                  fillOpacity={0.55}
                />
              )
            })}
          </g>
        )
      })}

      {/* Дорога: кромка и полотно. Стыки круглые — на повороте дорога
          остаётся дорогой, а не даёт угол шириной в клетку. */}
      <path
        d={road}
        fill="none"
        stroke="var(--road-line)"
        strokeWidth={1.02}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path
        d={road}
        fill="none"
        stroke="var(--road)"
        strokeWidth={0.9}
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* Деления: без них шаги не посчитать глазами. На старте и входе их
          нет — там своя метка. */}
      {board.ring.map((cell, index) => {
        if (marks.starts.has(index) || marks.entries.has(index)) return null

        const point = center(cell)

        return (
          <circle
            key={`tick-${cell.row}-${cell.col}`}
            cx={point.x}
            cy={point.y}
            r={0.075}
            fill="var(--tick)"
            fillOpacity={0.8}
          />
        )
      })}

      {/*
        Старт — ворота со стрелкой хода: доска сама показывает, куда идут фишки.

        Клетка ЗАЛИТА СВЕТЛЫМ, как лента дома и вход в неё, а не своим цветом
        в полную силу. Залитая в полную силу, она была того же цвета, что
        и фишка, которая на ней стоит, — а стоит она там всю дорогу, пока
        игрок заводит фишки: круг сливался с квадратом, и фишку на своём
        старте было не найти. Цвет держат обводка и стрелка, а полную силу
        цвета на доске носит только фишка.

        Стрелка при этом залитая, а у входа в дом — контурная: обе клетки
        теперь светлые, и различать их должно что-то ещё.
      */}
      {[...marks.starts].map(([index, colour]) => {
        const cell = board.ring[index]
        const next = board.ring[(index + 1) % board.ring.length]
        const point = center(cell)

        return (
          <g key={`start-${colour}`}>
            <rect
              x={cell.col + 0.07}
              y={cell.row + 0.07}
              width={0.86}
              height={0.86}
              rx={0.3}
              fill={PALETTE[colour].fill}
              fillOpacity={0.26}
              stroke={PALETTE[colour].line}
              strokeOpacity={0.8}
              strokeWidth={0.08}
            />
            <path
              d="M -0.11 -0.18 L 0.14 0 L -0.11 0.18 Z"
              fill={PALETTE[colour].line}
              fillOpacity={0.9}
              transform={`translate(${point.x} ${point.y}) rotate(${angleOf(
                next.col - cell.col,
                next.row - cell.row,
              )})`}
            />
          </g>
        )
      })}

      {/* Вход в дом — стрелка внутрь, в сторону своей дорожки. Пунктирная
          рамка была неотличима от рамки любой другой клетки. */}
      {[...marks.entries].map(([index, player]) => {
        if (marks.starts.has(index)) return null

        const cell = board.ring[index]
        const first = player.home[0]
        const point = center(cell)

        return (
          <g key={`entry-${player.color}`}>
            <rect
              x={cell.col + 0.07}
              y={cell.row + 0.07}
              width={0.86}
              height={0.86}
              rx={0.3}
              fill={PALETTE[player.color].fill}
              fillOpacity={0.3}
            />
            <path
              d="M -0.16 -0.2 L 0.12 0 L -0.16 0.2"
              fill="none"
              stroke={PALETTE[player.color].line}
              strokeWidth={0.11}
              strokeLinecap="round"
              strokeLinejoin="round"
              transform={`translate(${point.x} ${point.y}) rotate(${angleOf(
                first.col - cell.col,
                first.row - cell.row,
              )})`}
            />
          </g>
        )
      })}

      {/* Внутренние углы: общая клетка двух сторон, отсюда прыгают по углам. */}
      {board.innerCorners.map((corner) => {
        const point = center(corner.cell)

        return (
          <circle
            key={`corner-${corner.index}`}
            cx={point.x}
            cy={point.y}
            r={0.34}
            fill="none"
            stroke="var(--yellow)"
            strokeWidth={0.09}
            strokeOpacity={0.9}
          />
        )
      })}
    </>
  )
})

/**
 * Пузырь реплики над карточкой игрока.
 *
 * Размеры — в пикселях телефонной доски, как и у карточки (CARD), и по той
 * же причине: двор у креста и у меандра разного размера, а реплика — одна
 * и та же.
 */
const BUBBLE = {
  /** Кегль. Мельче счёта во дворе: реплика — не подпись к игроку. */
  size: 12,
  /** Ширина, в которую вписывается текст; за ней — многоточие. */
  room: 104,
  /** Поле слева и справа от текста. */
  padding: 7,
  /** Высота плашки. */
  height: 22,
  /** Зазор между плашкой и подносом двора. */
  gap: 5,
}

/** Текст реплики в отведённую ширину: обрезка, а не уменьшение кегля. */
function fitSpeech(text: string): string {
  const flat = text.trim()

  if (textWidth(flat, BUBBLE.size) <= BUBBLE.room) return flat

  let fits = flat.length

  while (fits > 1 && textWidth(`${flat.slice(0, fits).trimEnd()}…`, BUBBLE.size) > BUBBLE.room) {
    fits -= 1
  }

  return `${flat.slice(0, fits).trimEnd()}…`
}

/**
 * Реплика — плашкой у двора того, кто её сказал.
 *
 * Так разговор виден, не открывая шторку, и сразу понятно, кто говорит.
 * Живёт она несколько секунд (features/chat/useSpeech.ts), поэтому лежит
 * в подвижном слое, а не в BoardStatic: тот запомнен нарочно и на каждую
 * реплику перерисовываться не должен.
 *
 * Сторона выбирается по половине доски: у нижних дворов плашка над
 * карточкой, у верхних — под ней. Иначе у верхних она уезжала за край плиты.
 */
function Speech({
  board,
  colour,
  text,
  box,
}: {
  board: BoardGeometry
  colour: PlayerColor
  text: string
  box: { x: number; y: number; width: number; height: number }
}) {
  const cells = (px: number) => inCells(board, px)
  const shown = fitSpeech(text)
  const width = cells(textWidth(shown, BUBBLE.size) + BUBBLE.padding * 2)
  const height = cells(BUBBLE.height)
  const middle = box.x + box.width / 2
  const below = box.y < board.size / 2
  const top = below ? box.y + box.height + cells(BUBBLE.gap) : box.y - height - cells(BUBBLE.gap)

  return (
    <g pointerEvents="none">
      <rect
        x={middle - width / 2}
        y={top}
        width={width}
        height={height}
        rx={height / 2}
        fill="var(--card)"
        stroke={PALETTE[colour].line}
        strokeOpacity={0.65}
        strokeWidth={0.06}
      />
      <text
        className="board__card"
        x={middle}
        y={top + height / 2}
        dy={cells(BUBBLE.size) * BASELINE}
        textAnchor="middle"
        fontSize={cells(BUBBLE.size)}
        fill="var(--ink)"
      >
        {shown}
      </text>
    </g>
  )
}

/**
 * Подсказка обучения: карточка с текстом и изогнутая стрелка от неё к цели.
 *
 * Карточка — и есть объяснение: заголовком то, что сделать сейчас, под ним
 * зачем. Отдельной карточки под доской нет: на телефоне взгляд оттуда до
 * фишки и обратно — это полэкрана, и читали либо текст, либо доску.
 *
 * Стоит не над целью, а наискосок и ближе к середине доски: над самой целью
 * закрывала бы соседние клетки, у края уезжала бы за него. Стрелка выходит
 * из карточки вертикально и приходит к цели под углом — дуга читается как
 * жест «вот сюда», прямая — как разметка.
 *
 * Размеры в пикселях телефонной доски (360 в ширину): mobile first, на
 * широком экране всё растёт вместе с доской.
 */
const GUIDE = {
  title: 15,
  body: 13,
  /** Высота строки в долях кегля. */
  line: 1.3,
  padX: 12,
  padY: 9,
  gap: 3,
  /** Предельная ширина карточки: чуть меньше двух третей телефонной доски. */
  width: 224,
  radius: 12,
  /** Насколько карточка отстоит от цели по вертикали и в сторону. */
  rise: 30,
  shift: 26,
  /** Толщина стрелки и длина пера наконечника. */
  stroke: 3,
  head: 9,
}

/** Разбить текст на строки не шире room пикселей — по словам. */
function wrap(text: string, size: number, room: number): string[] {
  const lines: string[] = []
  let line = ''

  for (const word of text.split(/\s+/).filter((one) => one !== '')) {
    const next = line === '' ? word : `${line} ${word}`

    if (line !== '' && textWidth(next, size) > room) {
      lines.push(line)
      line = word
    } else {
      line = next
    }
  }

  if (line !== '') lines.push(line)

  return lines
}

/** Цель указателя: точка, куда смотрит стрелка, и окно в затемнении вокруг. */
type GuideTarget = {
  x: number
  y: number
  /** Стрелка останавливается на этом расстоянии от точки — у края цели. */
  reach: number
  /** Цель — кубики: карточке тогда можно на них самих. */
  dice?: boolean
  hole: { x: number; y: number; width: number; height: number; round: number }
}

function Guide({
  board,
  target,
  guide,
  shadow,
  scale,
}: {
  board: BoardGeometry
  target: GuideTarget
  guide: BoardGuide
  /** id фильтра тени: он объявлен один раз на доску. */
  shadow: string
  /** Во сколько раз доска увеличена: подсказка остаётся своего размера. */
  scale: number
}) {
  const cells = (px: number) => inCells(board, px) / scale
  const room = GUIDE.width - GUIDE.padX * 2
  const lines = guide.text === undefined ? [] : wrap(guide.text, GUIDE.body, room)
  const inner = Math.min(
    room,
    Math.max(
      textWidth(guide.title, GUIDE.title),
      ...lines.map((line) => textWidth(line, GUIDE.body)),
    ),
  )
  const width = cells(inner + GUIDE.padX * 2)
  const titleLine = cells(GUIDE.title * GUIDE.line)
  const bodyLine = cells(GUIDE.body * GUIDE.line)
  const height =
    cells(GUIDE.padY * 2) +
    titleLine +
    (lines.length === 0 ? 0 : cells(GUIDE.gap) + bodyLine * lines.length)
  const rise = cells(GUIDE.rise) + target.reach
  const { above, side, middle, top } = placeBubble(
    board,
    target,
    width,
    height,
    rise,
    cells(GUIDE.shift),
  )
  const left = middle - width / 2
  const sign = above ? -1 : 1
  // Стрелка выходит из кромки карточки над целью, но не из скругления.
  const corner = cells(GUIDE.radius) + cells(4)
  const start = {
    x: Math.min(Math.max(target.x - side * cells(8), left + corner), left + width - corner),
    y: above ? top + height : top,
  }
  // Дуга гнётся на три четверти пути: карточка бывает и далеко от цели.
  const bend = {
    x: start.x,
    y: start.y + sign * (Math.abs(target.y - start.y) - target.reach) * 0.75,
  }
  // Конец — на краю цели, по направлению прихода дуги.
  const dx = target.x - bend.x
  const dy = target.y - bend.y
  const long = Math.hypot(dx, dy) || 1
  const end = {
    x: target.x - (dx / long) * target.reach,
    y: target.y - (dy / long) * target.reach,
  }
  const angle = Math.atan2(end.y - bend.y, end.x - bend.x)
  const head = cells(GUIDE.head)
  const wing = (turn: number) =>
    `${end.x - head * Math.cos(angle + turn)} ${end.y - head * Math.sin(angle + turn)}`
  const curve = `M ${start.x} ${start.y} Q ${bend.x} ${bend.y} ${end.x} ${end.y}`
  const tip = `M ${wing(0.5)} L ${end.x} ${end.y} L ${wing(-0.5)}`
  const stroke = cells(GUIDE.stroke)
  // Покачивание — вдоль стрелки, к цели и обратно.
  const nudge = {
    '--nudge-x': `${Math.cos(angle) * cells(3)}px`,
    '--nudge-y': `${Math.sin(angle) * cells(3)}px`,
  } as CSSProperties
  const textX = left + cells(GUIDE.padX)
  const titleY = top + cells(GUIDE.padY) + titleLine / 2
  const bodyY = top + cells(GUIDE.padY) + titleLine + cells(GUIDE.gap) + bodyLine / 2

  return (
    <g pointerEvents="none">
      <g className="ludo-guide" style={nudge}>
        {/* Подложка цветом карточки: стрелка видна и на тёмном, и на пёстром. */}
        <path
          d={curve}
          fill="none"
          stroke="var(--card)"
          strokeWidth={stroke * 2.2}
          strokeLinecap="round"
          opacity={0.9}
        />
        <path
          d={tip}
          fill="none"
          stroke="var(--card)"
          strokeWidth={stroke * 2.2}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.9}
        />
        <path
          className="ludo-guide__draw"
          d={curve}
          pathLength={1}
          fill="none"
          stroke="var(--accent)"
          strokeWidth={stroke}
          strokeLinecap="round"
        />
        <path
          className="ludo-guide__tip"
          d={tip}
          fill="none"
          stroke="var(--accent)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <g className="ludo-guide__bubble">
        <rect
          x={left}
          y={top}
          width={width}
          height={height}
          rx={cells(GUIDE.radius)}
          fill="var(--card)"
          stroke="var(--accent)"
          strokeWidth={cells(1.5)}
          filter={`url(#${shadow})`}
        />
        <text
          className="board__card"
          x={textX}
          y={titleY}
          dy={cells(GUIDE.title) * BASELINE}
          fontSize={cells(GUIDE.title)}
          fill="var(--accent)"
        >
          {guide.title}
        </text>
        {lines.length > 0 && (
          <text
            className="board__tip"
            x={textX}
            y={bodyY}
            dy={cells(GUIDE.body) * BASELINE}
            fontSize={cells(GUIDE.body)}
            fill="var(--ink)"
          >
            {lines.map((line, at) => (
              // Строки — куски одного текста, другого ключа у них нет.
              // biome-ignore lint/suspicious/noArrayIndexKey: см. выше
              <tspan key={`line-${at}`} x={textX} dy={at === 0 ? undefined : bodyLine}>
                {line}
              </tspan>
            ))}
          </text>
        )}
      </g>
    </g>
  )
}

/**
 * Где встать карточке подсказки.
 *
 * Перебор от ближнего к дальнему: над целью или под ней, наискосок к
 * середине доски, ровно над целью или наискосок к краю — и если нигде не
 * встать, то же самое чуть дальше от цели. Берётся первое место, где
 * карточка целиком на доске и не закрывает кубики: они — HTML поверх
 * картинки, легли бы на текст, а их значения как раз и нужны, чтобы понять
 * подсказку («теперь двойкой»). Подсказка про сами кубики их не избегает.
 *
 * На телефоне карточка в две трети доски, и обойти кубики сбоку ей негде —
 * поэтому отступ растёт по вертикали, а стрелка просто становится длиннее.
 */
function placeBubble(
  board: BoardGeometry,
  target: GuideTarget,
  width: number,
  height: number,
  rise: number,
  shift: number,
): { above: boolean; side: number; middle: number; top: number } {
  const half = board.size / 2
  const inward = target.x < half ? 1 : -1
  // Где лежат кубики — измерено в браузере (.centre-dice), с запасом на
  // кувырок броска.
  const dice = { left: half - 2.6, right: half + 2.6, top: half - 1.4, bottom: half + 1.7 }
  const clamp = (x: number) => Math.min(Math.max(x, width / 2 - 0.3), board.size + 0.3 - width / 2)
  const places: { above: boolean; side: number; middle: number; top: number }[] = []

  for (let farther = 0; farther <= 10; farther++) {
    for (const above of [true, false]) {
      for (const side of [inward, 0, -inward]) {
        const top = above ? target.y - rise - farther - height : target.y + rise + farther

        places.push({
          above,
          side: side === 0 ? inward : side,
          middle: clamp(target.x + side * shift),
          top,
        })
      }
    }
  }

  const inside = (place: (typeof places)[number]) =>
    place.top > -0.4 && place.top + height < board.size + 0.4
  const clear = (place: (typeof places)[number]) =>
    target.dice === true ||
    place.middle + width / 2 < dice.left ||
    place.middle - width / 2 > dice.right ||
    place.top + height < dice.top ||
    place.top > dice.bottom

  return places.find((place) => inside(place) && clear(place)) ?? places.find(inside) ?? places[0]
}

/**
 * Куда показывает стрелка.
 *
 * Фишка во дворе спрятана в карточку (см. отрисовку фишек), и нажимают там
 * весь двор — на него и показываем, и окно в затемнении — весь двор.
 * Кубики лежат чуть ниже центра доски (.centre-dice в ui.css).
 */
function guideTarget(
  board: BoardGeometry,
  guide: BoardGuide,
  tokens: PlacedToken[],
  seated: boolean,
): GuideTarget | null {
  const around = (x: number, y: number, radius: number): GuideTarget => ({
    x,
    y,
    reach: radius,
    hole: {
      x: x - radius,
      y: y - radius,
      width: radius * 2,
      height: radius * 2,
      round: radius,
    },
  })

  if ('dice' in guide) {
    const middle = board.size / 2

    return { ...around(middle, middle + 0.4, 2.3), dice: true }
  }

  if ('cell' in guide) {
    const point = center(guide.cell)

    return around(point.x, point.y, 0.8)
  }

  if ('yard' in guide) return yardTarget(board, guide.yard)

  const placed = tokens.find((one) => one.color === guide.color && one.token === guide.token)

  if (placed === undefined) return null

  if (seated && placed.waiting) return yardTarget(board, guide.color)

  const point = center(placed.cell)

  return around(point.x, point.y, 0.8)
}

/** Весь двор игрока — цель и окно в затемнении. */
function yardTarget(board: BoardGeometry, colour: PlayerColor): GuideTarget | null {
  const yard = board.players.find((player) => player.color === colour)

  if (yard === undefined) return null

  const box = yardBox(board, yard.base.rect)
  const pad = 0.3

  return {
    x: box.x + box.width / 2,
    y: box.y + box.height / 2,
    reach: box.height / 2 + pad,
    hole: {
      x: box.x - pad,
      y: box.y - pad,
      width: box.width + pad * 2,
      height: box.height + pad * 2,
      round: Math.min(1, box.width * 0.2) + pad,
    },
  }
}

/**
 * Затемнение с окнами: доска чуть гаснет, светлыми остаются только цели
 * указателей. Взгляд идёт туда, где действие, а доска остаётся видна —
 * позиция нужна, чтобы понять, зачем этот ход.
 */
function Spotlight({ board, targets }: { board: BoardGeometry; targets: GuideTarget[] }) {
  const id = useId()

  return (
    <g pointerEvents="none" className="ludo-spot">
      <mask id={`${id}-spot`}>
        <rect x={-0.5} y={-0.5} width={board.size + 1} height={board.size + 1} fill="#fff" />
        {targets.map((target) => (
          <rect
            key={`hole-${target.x}-${target.y}`}
            className="ludo-spot__hole"
            {...target.hole}
            rx={target.hole.round}
            fill="#000"
          />
        ))}
      </mask>
      <rect
        x={-0.5}
        y={-0.5}
        width={board.size + 1}
        height={board.size + 1}
        fill="#000"
        opacity={0.38}
        mask={`url(#${id}-spot)`}
      />
    </g>
  )
}

type Props = {
  board: BoardGeometry
  /** Фишки уже в координатах клеток: правила считает не доска. */
  tokens?: PlacedToken[]
  /** Кто за столом: во дворе каждого рисуется его карточка. */
  people?: BoardPerson[]
  /** Чей сейчас ход: его угол подсвечивается. */
  current?: PlayerColor | null
  /** Что сказали только что — плашкой у двора говорившего. */
  speech?: { color: PlayerColor; text: string }[]
  /** Куда можно сходить выбранной фишкой. */
  targets?: MoveTarget[]
  onPickToken?: (token: PlacedToken) => void
  onPickTarget?: (key: string) => void
  /** Клик мимо фишек и целей — снять выбор. */
  onPickBackground?: () => void
  /**
   * Значки на фишках по цветам игроков (features/shop): код вещи, null —
   * без значка. Только значок: цвет фишки — это игрок, и он не меняется.
   */
  emblems?: Partial<Record<PlayerColor, string | null>>
  /**
   * Бонусы на поле (режим с бонусами, docs/BONUSES.md): номер клетки
   * кольца и вид. Номер, а не клетка: раскладку по виду поля знает доска.
   */
  bonuses?: { cell: number; kind: string; expiring?: boolean }[]
  /** Фишки под щитом и сколько ходов владельца щит ещё продержится. */
  shields?: { color: PlayerColor; token: number; left?: number }[]
  /** Клетки кольца, где только что забрали бонус: там расходится кольцо. */
  flashes?: number[]
  /** Стрелки обучения: что нажать сейчас. */
  guides?: BoardGuide[]
  /** Цель, на которой сейчас палец: видно её путь, остальные цели гаснут. */
  pressed?: string | null
  onPressTarget?: (key: string | null) => void
  /**
   * Во сколько раз доска увеличена (камера телефона): подсказки обучения
   * рисуются в клетках доски и без поправки выросли бы вместе с ней.
   */
  guideScale?: number
}

export function BoardView({
  board,
  tokens = [],
  people = [],
  current = null,
  speech = [],
  targets = [],
  onPickToken,
  onPickTarget,
  onPickBackground,
  emblems = {},
  bonuses = [],
  shields = [],
  flashes = [],
  guides = [],
  pressed = null,
  onPressTarget,
  guideScale = 1,
}: Props) {
  /*
   * Зоны нажатия считаются по всем нажимаемым точкам разом — и по фишкам, и
   * по целям: за палец они конкурируют друг с другом, а не каждый со своими.
   */
  // Прячем ли фишки во дворе: считается один раз, иначе в зависимостях
  // useMemo оказывался бы весь список игроков ради одного признака.
  const seated = people.length > 0
  /*
   * Число на клетке-цели и плашка под ним.
   *
   * Задано долей клетки, но не мельче стольких-то пикселей телефонной доски,
   * и вот почему нужно и то и другое. Доля клетки одна, а клетка у видов поля
   * разная: у креста 13,8 пикселя, у меандра 20. Прежние 0,62 клетки давали
   * на кресте 8,6 пикселя — самый мелкий текст во всей игре, и стоял он там,
   * где по нему решают, каким кубиком идти. На меандре те же 0,62 дают 12,4,
   * и уменьшать их незачем.
   *
   * Отсюда максимум из двух: на крупной клетке число растёт вместе с ней,
   * на мелкой упирается в пол. Плашка — кружок в углу клетки, наружу ей
   * выходить можно, поэтому пол есть и у неё.
   */
  const shadow = `${useId()}-guide-shadow`
  // Стрелки обучения с их целями; у спрятанной фишки цели может не быть.
  const aims = guides.flatMap((guide) => {
    const target = guideTarget(board, guide, tokens, seated)

    return target === null ? [] : [{ guide, target }]
  })
  const pips = Math.max(0.62, inCells(board, 11))
  const badge = Math.max(0.46, inCells(board, 8))
  const reach = useMemo(
    () =>
      reaches([
        ...tokens
          // Спрятанные в карточку не считаются: по ним не нажимают, а место
          // у соседей они бы отняли.
          .filter((placed) => placed.pickable && !(seated && placed.waiting))
          .map((placed) => ({ key: tokenKey(placed), cell: placed.cell })),
        ...targets.map((target) => ({ key: target.key, cell: target.cell })),
      ]),
    [tokens, targets, seated],
  )

  return (
    <div>
      {/* Клик мимо фигур и Escape снимают выбор фишки — иначе отменить его
          можно только повторным попаданием по ней же. */}
      {/*
        Размер — в стилях (.board), а не здесь: он зависит и от ширины
        колонки, и от высоты окна, а инлайновые 46rem учитывали только
        первое и упирали доску в них же на любом мониторе.
      */}
      <svg
        className="board"
        viewBox={`-0.5 -0.5 ${board.size + 1} ${board.size + 1}`}
        role="img"
        aria-label="Доска"
        onClick={() => onPickBackground?.()}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onPickBackground?.()
        }}
      >
        {/* Мерцание — стилями, а не размером и не цветом: увеличенная фишка
            лезет на соседние клетки, а перекраска в чёрный ломает главное,
            что фишка сообщает, — чей она. */}
        <style>{`
          @keyframes ludo-blink { 0%, 100% { opacity: 1 } 50% { opacity: 0.3 } }
          .ludo-blink { animation: ludo-blink 0.9s ease-in-out infinite }
          @keyframes ludo-guide { 0%, 100% { transform: translate(0, 0) } 50% { transform: translate(var(--nudge-x), var(--nudge-y)) } }
          .ludo-guide { animation: ludo-guide 1.1s ease-in-out 0.6s infinite }
          @keyframes ludo-draw { from { stroke-dashoffset: 1 } to { stroke-dashoffset: 0 } }
          .ludo-guide__draw { stroke-dasharray: 1; animation: ludo-draw 0.45s ease-out 0.15s both }
          @keyframes ludo-fade { from { opacity: 0 } to { opacity: 1 } }
          .ludo-guide__tip { animation: ludo-fade 0.2s ease-out 0.5s both }
          @keyframes ludo-pop { 0% { transform: scale(0.4); opacity: 0 } 70% { transform: scale(1.08); opacity: 1 } 100% { transform: scale(1) } }
          .ludo-guide__bubble { transform-box: fill-box; transform-origin: center; animation: ludo-pop 0.35s ease-out both }
          .ludo-spot { animation: ludo-fade 0.4s ease-out both }
          @keyframes ludo-aim { 0% { transform: scale(1); opacity: 0.9 } 100% { transform: scale(1.9); opacity: 0 } }
          .ludo-aim { transform-box: fill-box; transform-origin: center; animation: ludo-aim 1.4s ease-out infinite }
          @media (prefers-reduced-motion: reduce) {
            .ludo-blink, .ludo-guide, .ludo-guide__draw, .ludo-guide__tip, .ludo-guide__bubble, .ludo-spot, .ludo-aim { animation: none }
          }
        `}</style>

        <BoardStatic board={board} people={people} current={current} />

        {/*
          Прыжок по углам показывается дугой от фишки к углу. Без неё он
          неотличим от обычного хода: фишка просто перелетает клетки, и это
          выглядит как проход сквозь чужие и свои — хотя по правилам прыжку
          мешают только занятые углы.
        */}
        {targets
          .filter((target) => target.jump)
          .map((target) => {
            const from = tokens.find((placed) => placed.selected)

            if (from === undefined) return null

            const a = center(from.cell)
            const b = center(target.cell)
            // Прогиб в сторону от прямой: дуга читается как перелёт, а не
            // как черта, проведённая по доске.
            const bend = {
              x: (a.x + b.x) / 2 + (b.y - a.y) / 6,
              y: (a.y + b.y) / 2 - (b.x - a.x) / 6,
            }

            return (
              <path
                key={`arc-${target.key}`}
                d={`M ${a.x} ${a.y} Q ${bend.x} ${bend.y} ${b.x} ${b.y}`}
                fill="none"
                stroke={targetColor(target)}
                strokeWidth={0.14}
                strokeDasharray="0.34 0.28"
                strokeLinecap="round"
                opacity={0.85}
                pointerEvents="none"
              />
            )
          })}

        {/*
          Бонусы — под фишками и метками целей: фишка, вставшая на клетку,
          бонус забирает, и закрывать её значком незачем.
        */}
        {bonuses.map((bonus) => {
          const cell = board.ring[bonus.cell]

          if (cell === undefined) return null

          const point = center(cell)

          return (
            <g
              key={`bonus-${bonus.cell}`}
              className="bonus"
              transform={`translate(${point.x} ${point.y})`}
              pointerEvents="none"
            >
              {/* Последний круг — бонус «догорает»: мигает и тлеет
                  кольцом. Иначе он пропадал с поля без предупреждения, и
                  было непонятно, успеешь ли дойти. Мигание на вложенной
                  группе: у внешней своя анимация появления. */}
              <g className={bonus.expiring === true ? 'bonus__burn' : undefined}>
                {bonus.expiring === true && <circle r={0.47} className="bonus__fuse" />}
                <BonusGlyph kind={bonus.kind} />
              </g>
            </g>
          )
        })}

        {/* Вспышка подбора: кольцо расходится от клетки и гаснет. */}
        {flashes.map((index) => {
          const cell = board.ring[index]

          if (cell === undefined) return null

          const point = center(cell)

          return (
            <circle
              key={`flash-${index}`}
              className="bonus-flash"
              cx={point.x}
              cy={point.y}
              r={0.5}
              pointerEvents="none"
            />
          )
        })}

        {/*
          Метки целей — под фишками, чтобы не закрывать ту, которую рубят.
          Пятном на дороге, а не рамкой в клетку: рамка была неотличима от
          рамки самой клетки, пока клетки рисовались рамками.
        */}
        {/*
          Путь цели, на которой палец: какая фишка пойдёт и по какой дороге.
          Только пока держат — постоянные пути всех вариантов рябили в глазах.
        */}
        {targets
          .filter((target) => target.key === pressed && (target.trail?.length ?? 0) > 0)
          .map((target) => {
            const points = (target.trail ?? [])
              .map(center)
              .map((point) => `${point.x},${point.y}`)
              .join(' ')

            return (
              <polyline
                key={`trail-${target.key}`}
                points={points}
                fill="none"
                stroke={targetColor(target)}
                strokeWidth={0.16}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeOpacity={0.9}
                pointerEvents="none"
              />
            )
          })}

        {targets.map((target) => {
          const color = targetColor(target)
          const point = center(target.cell)
          const faded = pressed !== null && target.key !== pressed

          return (
            <g key={`mark-${target.key}`} pointerEvents="none" opacity={faded ? 0.25 : 1}>
              <circle
                cx={point.x}
                cy={point.y}
                r={0.52}
                fill={color}
                fillOpacity={target.victims.length > 0 ? 0.24 : 0.17}
              />
              <circle
                cx={point.x}
                cy={point.y}
                r={0.52}
                fill="none"
                stroke={color}
                strokeWidth={target.victims.length > 0 ? 0.12 : 0.09}
                strokeDasharray={target.jump ? '0.16 0.12' : undefined}
                className="ludo-blink"
              />
            </g>
          )
        })}

        {/* Фишки. */}
        {tokens.map((placed) => {
          /*
           * Фишка во дворе спрятана В КАРТОЧКУ, а не выброшена из разметки.
           *
           * Своих мест во дворе у неё больше нет — их заменил счёт, — но
           * убирать её из дерева нельзя: тогда срубленная исчезала на месте,
           * а выходящая из двора появлялась на старте из ничего. Спрятанная
           * стоит в середине своей карточки прозрачной, и переход между
           * «на доске» и «в карточке» CSS делает сам: меняются transform
           * и opacity, а переход у фишки уже есть.
           */
          const tucked = seated && placed.waiting
          const point = tucked ? cardCentre(board, placed.color) : center(placed.cell)
          const skin = PALETTE[placed.color]
          const emblem = emblemPath(emblems[placed.color])
          const shield = shields.find(
            (one) => one.color === placed.color && one.token === placed.token,
          )
          const shielded = shield !== undefined
          const label = `фишка ${LABEL[placed.color]} №${placed.token + 1}`

          const body = (
            <>
              {/*
                Всё видимое тело фишки — одной группой.
                
                Подъём при ходе и покачивание во дворе висят на ней, а не на
                цветном круге: круг масштабировался в одиночку, блик оставался
                прежнего размера на прежнем месте, и при ходе фишка зримо
                разъезжалась со своим бликом. Блик — то, что делает фишку
                предметом, а не кружком, поэтому двигаться он обязан вместе
                с ней.
                
                Привязка по классу, а не по месту в группе: правила стояли на
                circle:first-child и ломались от любого элемента, добавленного
                перед фишкой, — ровно это и случилось, когда у ходячих фишек
                появился круг зоны нажатия.
              */}
              <g className="token__lift">
                {/*
                  Мерцание выбранной висит на группе, а не на цветном круге:
                  у фишки теперь есть кант и блик, и мерцать они обязаны
                  вместе с ней — иначе от фишки мигает только заливка.
                */}
                <g className={placed.selected ? 'ludo-blink' : undefined}>
                  {/* 0.44 вместо прежних 0.38: клеток на доске стало меньше
                      линий, и место, которое они занимали, уходит фишке —
                      на телефоне это 12,7 пикселя против 10,9. */}
                  <circle cx={0} cy={0} r={0.44} fill={skin.fill} />
                  {/* Кант по краю: он отделяет фишку от дороги, на которой
                      она лежит, надёжнее тени. */}
                  <circle
                    cx={0}
                    cy={0}
                    r={0.44}
                    fill="none"
                    stroke={skin.rim}
                    strokeWidth={0.07}
                    strokeOpacity={0.55}
                  />
                  {/* Значок поверх цвета, под бликом: блик — свет на
                      предмете, и лежать он должен поверх всего. */}
                  {emblem !== null && <path d={emblem} fill="#fff" opacity={0.85} />}
                  {/* Блик сверху слева: с ним фишка читается как предмет,
                      а не как крашеный кружок. */}
                  <ellipse cx={-0.11} cy={-0.15} rx={0.19} ry={0.13} fill="#fff" opacity={0.4} />
                </g>
                {/* Щит — значком в углу, а не обводкой: обводку своего цвета
                    уже носит фишка, которой можно сходить, и два кольца
                    вокруг одной фишки не различить. */}
                {/* Цифра рядом — сколько ходов владельца щит ещё продержится;
                    на последнем ходу значок мигает, как догорающий бонус. */}
                {shielded && (
                  <g
                    transform="translate(0.36 -0.36) scale(0.62)"
                    className={`bonus bonus--badge${shield.left === 1 ? ' bonus__burn' : ''}`}
                  >
                    <circle r={0.4} className="bonus__disc" />
                    <path d={GLYPHS.shield} className="bonus__glyph" transform="scale(1.35)" />
                    {shield.left !== undefined && shield.left > 1 && (
                      <g transform="translate(0.42 0.36)">
                        <circle r={0.26} className="bonus__count" />
                        <text className="bonus__count-text" textAnchor="middle" dy={0.11}>
                          {shield.left}
                        </text>
                      </g>
                    )}
                  </g>
                )}
                {/* Фишка, которой есть чем сходить, обведена пульсирующим
                  кольцом своего цвета: игрок должен видеть варианты, не
                  перебирая фишки по одной. Выбранная пульсирует быстрее и
                  вдобавок мерцает сама. */}
                {/*
                Подписи <title> у фишки нет.
                
                Читалке она всё равно ничего не давала: у самого svg стоит
                role="img", а внутри такой роли содержимое считается единым
                изображением и не озвучивается. Работала она только как
                браузерная подсказка при наведении — над каждой из шестнадцати
                фишек всплывало «фишка красный №3». Имя для читалки несёт
                aria-label у группы там, где по фишке можно нажать.
              */}
                {/* Кольцо остаётся в пределах MIN_REACH: рисовать зону
                    больше той, по которой ловится нажатие, нельзя. */}
                {placed.pickable && (
                  <circle
                    cx={0}
                    cy={0}
                    r={0.5}
                    fill="none"
                    stroke={skin.line}
                    strokeWidth={0.06}
                    className={placed.selected ? 'ludo-blink' : 'ludo-ready'}
                  />
                )}
              </g>
            </>
          )

          // Чужие и неходячие фишки — просто картинка: обработчики вешаются
          // только на те, которыми сейчас можно сходить.
          // Позиция задаётся стилем, а не координатами фигур: так браузер
          // сам плавно доводит фишку от клетки к клетке, а React на шаге
          // анимации меняет одно свойство вместо пересборки кружков.
          /*
           * Место и видимость — в разметке, а длительность перехода — в стилях
           * (.token и .token--tucked).
           *
           * Разделено ради приведённого движения: шаг по клетке идёт в такт
           * с показом (--step, он же у подскока), а полёт в карточку длиннее —
           * это не шаг, а дорога через полдоски, и на скорости шага она
           * читалась бы как телепорт. Инлайновый переход к тому же нельзя было
           * отменить для prefers-reduced-motion иначе как !important.
           */
          const at = {
            transform: `translate(${point.x}px, ${point.y}px)`,
            opacity: tucked ? 0 : 1,
          }
          const skinClass = [
            'token',
            placed.moving ? 'token--moving' : '',
            // Покачивание — только у видимых во дворе. Спрятанная в карточку
            // качалась бы вечно и незримо: до двадцати анимаций на кадр
            // ради того, чего не видно.
            placed.waiting && !tucked ? 'token--waiting' : '',
            tucked ? 'token--tucked' : '',
          ]
            .filter(Boolean)
            .join(' ')

          // Покачивание во дворе идёт вразнобой: одинаковый ритм у всех
          // выглядит как механизм, а не как ждущие своей очереди фишки.
          const bob = placed.waiting
            ? ({ '--bob': `${(placed.token % 5) * 0.35}s` } as CSSProperties)
            : undefined

          if (!placed.pickable || tucked) {
            return (
              <g key={tokenKey(placed)} className={skinClass} style={{ ...at, ...bob }}>
                {body}
              </g>
            )
          }

          return (
            <g
              key={tokenKey(placed)}
              className={skinClass}
              style={{ ...at, ...bob, cursor: 'pointer' }}
              onClick={(event) => {
                event.stopPropagation()
                onPickToken?.(placed)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') onPickToken?.(placed)
              }}
              role="button"
              tabIndex={0}
              aria-label={label}
            >
              {/*
                Зона нажатия — прозрачным кругом ПОД кружком фишки: пальцем
                в нарисованные 0.38 клетки (на телефоне это около пяти
                пикселей радиусом) не попасть. Радиус свой у каждой фишки,
                см. reaches(). Круг, а не квадрат: фишка круглая, и зона
                расходится от неё ровно, без углов, которые лезли бы
                к соседям дальше, чем нужно.
              */}
              <circle
                className="token__reach"
                cx={0}
                cy={0}
                r={reach.get(tokenKey(placed)) ?? MIN_REACH}
                fill="transparent"
              />
              {body}
            </g>
          )
        })}

        {/*
          Число хода — ПОВЕРХ фишек.
          
          Под ними оно тонуло на клетке, которую рубят: там стоит чужая фишка,
          и подпись оказывалась под ней. Плашкой цвета хода, а не вывороткой
          на подложке карточки: пятно метки под фишкой уже не видно, и число
          осталось единственным, что говорит «на сколько».
        */}
        {targets.map((target) => {
          const color = targetColor(target)
          const point = center(target.cell)

          return (
            <g
              key={`pips-${target.key}`}
              pointerEvents="none"
              opacity={pressed !== null && target.key !== pressed ? 0.25 : 1}
            >
              <circle cx={point.x + 0.44} cy={point.y - 0.44} r={badge} fill={color} />
              <text
                className="board__pips"
                x={point.x + 0.44}
                y={point.y - 0.44}
                dy={pips * BASELINE}
                textAnchor="middle"
                fontSize={pips}
                fill="var(--card)"
              >
                {target.pips}
              </text>
            </g>
          )
        })}

        {/*
          Двор — кнопка «завести фишку в игру».

          Отдельных кружков во дворе больше нет: их заменил счёт в карточке
          (см. Card). Значит и нажать по ждущей фишке стало не по чему, а
          вывести её из двора надо — без этого партию не начать вовсе.
          Поэтому нажимается весь двор, а фишка выбирается сама: ждущие фишки
          неотличимы друг от друга, и выбирать между ними нечего.

          Только когда за столом кто-то есть: без карточек кружки во дворе
          рисуются по-прежнему и нажимаются сами, а ловушка на весь двор их
          бы накрыла.
        */}
        {people.length > 0 &&
          board.players.map((player) => {
            const ready = tokens.filter(
              (placed) => placed.color === player.color && placed.waiting && placed.pickable,
            )
            // Выбранную предпочитаем первой: повторное нажатие по двору должно
            // снимать выбор, а не выбирать соседнюю фишку.
            const pick = ready.find((placed) => placed.selected) ?? ready[0]

            if (pick === undefined) return null

            const box = yardBox(board, player.base.rect)

            return (
              <g
                key={`yard-${player.color}`}
                onClick={(event) => {
                  event.stopPropagation()
                  onPickToken?.(pick)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onPickToken?.(pick)
                }}
                role="button"
                tabIndex={0}
                aria-label={`завести фишку ${LABEL[player.color]} из двора`}
                style={{ cursor: 'pointer' }}
              >
                <rect
                  x={box.x}
                  y={box.y}
                  width={box.width}
                  height={box.height}
                  fill="transparent"
                />
                {/* Двор, из которого есть чем сходить, обведён тем же
                    пульсирующим кольцом, что и ходячая фишка: иначе о том,
                    что по нему можно нажать, узнать нечем. */}
                <rect
                  x={box.x}
                  y={box.y}
                  width={box.width}
                  height={box.height}
                  rx={Math.min(1, box.width * 0.2)}
                  fill="none"
                  stroke={PALETTE[player.color].line}
                  strokeWidth={0.12}
                  className={pick.selected ? 'ludo-blink' : 'ludo-ready'}
                  pointerEvents="none"
                />
              </g>
            )
          })}

        {/*
          Реплики — поверх фишек, но под ловушками нажатия: закрывать ими
          цель хода нельзя, а прочитать разговор поверх доски надо.
        */}
        {speech.length > 0 &&
          board.players.map((player) => {
            const said = speech.find((one) => one.color === player.color)

            if (said === undefined) return null

            return (
              <Speech
                key={`speech-${player.color}`}
                board={board}
                colour={player.color}
                text={said.text}
                box={yardBox(board, player.base.rect)}
              />
            )
          })}

        {/*
          Стрелки обучения — поверх фишек и реплик, но под ловушками нажатия:
          нажимают не по стрелке, а по тому, на что она показывает. Вокруг
          цели расходится кольцо — стрелку замечают, кольцо говорит «здесь».
        */}
        {aims.length > 0 && (
          <>
            <defs>
              <filter id={shadow} x="-30%" y="-30%" width="160%" height="190%">
                <feDropShadow dx="0" dy="0.12" stdDeviation="0.14" floodOpacity="0.45" />
              </filter>
            </defs>
            {/* Пока ходит соперник, доску не гасим: смотреть надо на всю. */}
            {aims.some((aim) => aim.guide.quiet !== true) && (
              <Spotlight
                board={board}
                targets={aims.filter((aim) => aim.guide.quiet !== true).map((aim) => aim.target)}
              />
            )}
            {aims.map(({ guide, target }) => (
              // Ключ с подписью: новый шаг — новая стрелка, и она заново
              // прорисовывается, а не молча переезжает.
              <g key={`guide-${target.x}-${target.y}-${guide.title}-${guide.text ?? ''}`}>
                {target.reach < 1 && (
                  <circle
                    className="ludo-aim"
                    cx={target.x}
                    cy={target.y}
                    r={0.5}
                    fill="none"
                    stroke="var(--accent)"
                    strokeWidth={0.12}
                    pointerEvents="none"
                  />
                )}
                <Guide
                  board={board}
                  target={target}
                  guide={guide}
                  shadow={shadow}
                  scale={guideScale}
                />
              </g>
            ))}
          </>
        )}

        {/* Ловушка нажатия — поверх всего и на целую клетку: иначе чужая
            фишка перехватывает клик, и срубить её можно только промахнувшись
            мимо неё в угол клетки. */}
        {targets.map((target) => (
          <g
            key={`hit-${target.key}`}
            onClick={(event) => {
              event.stopPropagation()
              onPressTarget?.(null)
              onPickTarget?.(target.key)
            }}
            onPointerDown={() => onPressTarget?.(target.key)}
            onPointerLeave={() => onPressTarget?.(null)}
            onPointerCancel={() => onPressTarget?.(null)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') onPickTarget?.(target.key)
            }}
            role="button"
            tabIndex={0}
            aria-label={target.hint}
            style={{ cursor: 'pointer' }}
          >
            {/*
              Запас — свой у каждой цели. Одинаковый для всех перекрывал
              соседние клетки, и нажатие по краю уходило не той цели: цели
              ходов часто стоят рядом. Половина расстояния до ближайшей
              соседней точки такого не допускает, а там, где по соседству
              никого, зона вырастает до двух клеток — см. reaches().
            */}
            <Reach cell={target.cell} radius={reach.get(target.key) ?? MIN_REACH} />
          </g>
        ))}
      </svg>
    </div>
  )
}
